import React from 'react';
import { useHookstate, type State } from "@hookstate/core";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemText, Typography } from "@mui/material";
import { useLang } from '~/hooks/useLang';
import { LoadingButton } from '@mui/lab';
import { useTheme } from "~/hooks/useTheme";
import { deactivateMultiple } from '../../services';
import CloseIcon from '@mui/icons-material/Close';
import type IUser from '../../interfaces/IUser';
import notify from '~/utils/notify';
import { capitalize } from '~/utils/strings';


interface DialogDeactivateMultipleUserProps {
  readonly openDialogListDeactivatedUsers: State<boolean>;
  readonly openModalResultMultipleDeactivatedUsers: State<boolean>;
  readonly rowsSelected: State<IUser[]>;
  readonly enableButtonShowResume: State<boolean>;
  readonly forceRefresh: State<boolean>
}

interface resultMultipleDeactivatedUsersProps {
  readonly user: string;
  readonly registration: string;
  readonly success: boolean;
  readonly error: string;
}

// eslint-disable-next-line max-lines-per-function
export default function DialogDeactivateMultipleUser({
  openDialogListDeactivatedUsers,
  openModalResultMultipleDeactivatedUsers,
  enableButtonShowResume,
  rowsSelected,
  forceRefresh,
}: DialogDeactivateMultipleUserProps) {

  const { translate } = useLang();
  const { darkMode } = useTheme();
  const loading = useHookstate(false);
  const resultMultipleDeactivatedUsers = useHookstate<resultMultipleDeactivatedUsersProps[]>([]);

  const handleDeactivateMultiple = async () => {
    loading.set(true)
    const userIds = rowsSelected.get().map((user) => Number(user.id))
    deactivateMultiple({ usersIds: userIds }).then((response) => {
      notify({
        message: `${translate('The batch deactivate will be processed and you will receive a notice when the process is complete')}`,
        type: 'info'
      })

      if (response.length > 0) {
        resultMultipleDeactivatedUsers.set(response)
        notify({
          message: `${translate('Multiple deactivate executed successfully!')}`,
          type: 'success',
        })
        enableButtonShowResume.set(true)
      }

    }).finally(() => {
      loading.set(false)
      handleCloseListDeactivatedUsers()
      forceRefresh.set(!forceRefresh.get())
    })

    return true
  }

  const clickOutModalCloseListDeactivateUsers = (event, reason) => {
      if (reason !== 'backdropClick') {
        openDialogListDeactivatedUsers.set(false);
      }
    };
  
    const clickOutModalCloseResultDeactivatedUsers = (event, reason) => {
      if (reason !== 'backdropClick') {
        openModalResultMultipleDeactivatedUsers.set(false);
      }
    };
  
    const handleCloseListDeactivatedUsers = () => {
      openDialogListDeactivatedUsers.set(false);
    };
  
    const handleCloseResultDeactivatedUsers = () => {
      openModalResultMultipleDeactivatedUsers.set(false);
    };

  return (
    <>
      <Dialog
        open={openDialogListDeactivatedUsers.get()}
        onClose={clickOutModalCloseListDeactivateUsers}
        disableEscapeKeyDown={true}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>
          {translate('Deactivate users')}
        </DialogTitle>
        <DialogContent>
          <Typography variant='subtitle1'>
            {translate('Summary of the selected users')}
          </Typography>
          <List
            sx={{
              width: '100%',
              maxHeight: '500px',
              bgcolor: darkMode ? 'transparent' : '#FFF',
              marginTop: 2,
              overflowY: 'auto',
              border: 1,
              borderColor: 'grey.500',
              borderRadius: 1
            }}
          >
            {rowsSelected?.get().map((item, index, { length }) => (
              <>
                <ListItem key={item.id}>
                  <ListItemText
                    sx={{ display: 'inline', maxHeight: '150px', overflow: 'auto', whiteSpace: 'pre-line' }}
                    primary={`${capitalize(translate('login'))}: ${item.login}`}
                    secondary={
                      <Typography>
                        {`${translate('Name')}: ${item.representation_name ? item.representation_name : translate('Not informed')}\n${translate('registration')}: ${item.registration ? item.registration : translate('Not informed')}`}
                      </Typography>
                    }
                  />
                </ListItem>
                {index + 1 != length &&
                  <Divider />
                }
              </>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingX: "24px" }}>
          <Button
            variant='outlined'
            onClick={() => handleCloseListDeactivatedUsers()}>
            {translate('Cancel')}
          </Button>
          <LoadingButton
            loading={loading.get()}
            variant='contained'
            onClick={() => handleDeactivateMultiple()}>
            {translate('Submit')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openModalResultMultipleDeactivatedUsers.get()}
        onClose={clickOutModalCloseResultDeactivatedUsers}
        disableEscapeKeyDown={true}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          {translate('Summary of last batch deactivate users')}
          <IconButton
            aria-label="close"
            onClick={() => {
              // openModalResultMultipleDeactivatedUsers.set(false)
              handleCloseResultDeactivatedUsers()
              enableButtonShowResume.set(false)
              rowsSelected.set([])
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {resultMultipleDeactivatedUsers.get().filter((element) => element.error === '').map(item => item.error).length > 0 ? translate('Devices disconnected with success') : translate('No users has been deactivated')}
          </Typography>
          {resultMultipleDeactivatedUsers.get().filter((element) => element.error === '').map(item => item.error).length > 0 &&
            <List
              sx={{
                width: '100%',
                maxHeight: '500px',
                bgcolor: darkMode ? 'transparent' : '#FFF',
                marginTop: 2,
                overflowY: 'auto',
                border: 1,
                borderColor: 'grey.500',
                borderRadius: 1,
              }}
            >
              {resultMultipleDeactivatedUsers.get()?.filter((element) => element.error === '').map((item, index, { length }) => (
                <>
                  <ListItem key={index} >
                    <ListItemText
                      sx={{
                        display: 'inline',
                        maxHeight: '150px',
                        overflow: 'auto',
                        whiteSpace: 'pre-line',
                      }}
                      primary={
                        <Typography>
                          {`${translate('User')}: ${item.user ? item.user : translate('Not informed')}\n${translate('Registration')}: ${item.registration ? item.registration : translate('Not informed')}`}
                        </Typography>
                      }
                    >
                      <Divider />
                    </ListItemText>
                  </ListItem>
                  {index + 1 != length &&
                    <Divider />
                  }
                </>
              ))}
            </List>
          }

          <Typography variant="body1" marginTop={2}>
            {resultMultipleDeactivatedUsers.get().filter((element) => element.error != '').map(item => item.error).length > 0 ? translate('Users that could not be deactivated') : translate('There were no closing errors')}
          </Typography>
          {resultMultipleDeactivatedUsers.get().filter((element) => element.error != '').map(item => item.error).length > 0 &&
            <List
              sx={{
                width: '100%',
                maxHeight: '500px',
                bgcolor: darkMode ? 'transparent' : '#FFF',
                marginTop: 2,
                overflowY: 'auto',
                border: 1,
                borderColor: 'grey.500',
                borderRadius: 1
              }}
            >
              {resultMultipleDeactivatedUsers.get()?.filter((element) => element.error ? element.error.length > 0 : '').map((item, index, { length }) => (
                <>
                  <ListItem key={index}>
                    <ListItemText
                      sx={{ display: 'inline', maxHeight: '150px', overflow: 'auto', whiteSpace: 'pre-line' }}
                      primary={`${translate('User')}: ${item.user ? item.user : translate('Not informed')}\n${translate('Registration')}: ${item.registration ? item.registration : translate('Not informed')}`}
                      secondary={
                        <Typography variant="body2" color='error'>
                          {`${translate('Error')}: ${translate(item.error ? item.error : '')}`}
                        </Typography>
                      }
                    />
                    <Divider />
                  </ListItem >
                  {index + 1 != length &&
                    <Divider />
                  }
                </>
              ))}
            </List>
          }
        </DialogContent>
      </Dialog>
    </>
  )
}